import { inject, Service } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Api } from '@api/api';
import {
  getCategoriesList,
  getCategoryBlog,
  getPost as getPostFn,
  getPostsByIds,
} from '@api/functions';
import type { BlogCategoryListItemDto, BlogPostListItemDto, PaginatedDtoOfBlogPostListItemDto } from '@api/models';
import { environment } from '../../environments/environment';

interface PaginatedData<T> { data?: T[]; pagesTotal?: number; page?: number; }

@Service()
export class BlogService {
  private http = inject(HttpClient);
  private api = inject(Api);

  private categories: BlogCategoryListItemDto[];
  private lastPosts: BlogPostListItemDto[];

  getCategories(): Observable<PaginatedData<BlogCategoryListItemDto>> {
    if (this.categories) {
      return of({ data: this.categories });
    }

    return this.api.invoke(getCategoriesList)
      .pipe( tap(response => this.categories = response.data) );
  }

  getCategory(slug: string) {
    return this.api.invoke(getCategoryBlog, { slug });
  }

  getPost(slug: string) {
    return this.api.invoke(getPostFn, { slug });
  }

  /**
   * A post carries the ids of the posts linked to it and nothing else, so the related-posts block
   * resolves them here. The response keeps the order the ids were given in.
   */
  getPostsByIds(ids: number[]): Observable<PaginatedDtoOfBlogPostListItemDto> {
    return this.api.invoke(getPostsByIds, { body: { ids: ids } });
  }

  getLastPosts(): Observable<PaginatedData<BlogPostListItemDto>> {
    if (this.lastPosts) {
      return of({ data: this.lastPosts });
    }

    return this.getPostsList({ lastPosts: 'true', limit: '5' })
      .pipe( tap(response => this.lastPosts = response.data) );
  }

  getPostsList(params?: Record<string, string | number | boolean>): Observable<PaginatedData<BlogPostListItemDto>> {
    return this.http
      .get<PaginatedData<BlogPostListItemDto>>(
        `${environment.apiHost}/api/v1/blog/posts`,
        { params: { ...(params ? params : {}) } }
      );
  }
}
